// client/src/components/TeamsScreen.js
import { useFetch } from '../hooks/useFetch';
import { Link } from 'react-router-dom';
import { PfShell } from './shell/PfShell';
import { PageTitle } from './shell/PageTitle';
import { Highlight } from './mischief/Highlight';
import { Sticker } from './mischief/Sticker';
import { Scrawl } from './mischief/Scrawl';

// App-level route (above team context), so PfShell renders without
// PfTopBar / PfTabBar — matches AnnouncementsHome. The title uses
// Highlight (yellow marker strip) to differentiate from AnnouncementsHome
// which uses per-card Highlight; here the page anchor gets the treatment.
//
// Card anatomy:
//   - team name in serif via PageTitle-adjacent sizing
//   - two Stickers: age group (default tone) + tier (accent tone)
//   - whole card is the Link — tap target = full card
//
// Sticker tones deliberately picked: age group reads as neutral metadata
// (default), tier reads as the "this team's level" badge (accent = purple).
// Not using win/loss/tie tones — those are reserved for game outcomes.

export function TeamsScreen() {
  const { data: teams, loading, error } = useFetch('/teams');

  if (loading) {
    return (
      <PfShell>
        <div className="px-5 pt-6 text-ink-sub">Loading teams…</div>
      </PfShell>
    );
  }

  if (error) {
    return (
      <PfShell>
        <div className="px-5 pt-6">
          <PageTitle>Couldn't load teams</PageTitle>
          <pre className="mt-4 text-sm text-red-700 whitespace-pre-wrap">{error}</pre>
        </div>
      </PfShell>
    );
  }

  if (teams.length === 0) {
    return (
      <PfShell>
        <div className="px-5 pt-6">
          <PageTitle>
            <Highlight>Teams</Highlight>
          </PageTitle>
          <div className="mt-8 text-ink-sub">
            No teams yet.{' '}
            <Scrawl rotate={-3}>check back soon!</Scrawl>
          </div>
        </div>
      </PfShell>
    );
  }

  return (
    <PfShell>
      <div className="px-5 pt-6 pb-10">
        <PageTitle>
          <Highlight>Teams</Highlight>
        </PageTitle>

        <ul className="mt-8 space-y-4">
          {teams.map((t) => (
            <li key={t.id}>
              <Link
                to={`/teams/${t.id}`}
                className="
                  block relative
                  rounded-2xl border border-ink/10 bg-white
                  px-5 py-4
                  shadow-[0_1px_0_rgba(0,0,0,0.04)]
                  active:scale-[0.99] transition-transform
                "
              >
                {/* Team name — serif, prominent, but smaller than PageTitle
                    so hierarchy stays clean */}
                <div className="font-serif text-2xl leading-tight text-ink">
                  {t.name}
                </div>

                {/* Sticker row: age group + tier. Wrapped in flex-wrap so
                    long age-group names don't push tier off the card on
                    narrow screens. gap-2 gives the tilted chips a little
                    breathing room from each other. */}
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {t.age_group_name && (
                    <Sticker tone="default">{t.age_group_name}</Sticker>
                  )}
                  {t.tier && (
                    <Sticker tone="accent">{t.tier}</Sticker>
                  )}
                  {/* sport_name deliberately omitted from the card face —
                      MCYH is single-sport (hockey), showing "Hockey" on
                      every card is noise. If we ever go multi-sport for
                      an org, add a third sticker here. */}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </PfShell>
  );
}